import type { Household } from '@/models/Household';
import type { UserProfile } from '@/models/supabase/User';
import { supabase, supabaseRpc } from '@/lib/supabase';

export const householdService = {
  // Créer un nouveau foyer
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async createHousehold(data: any): Promise<Household> {
    throw new Error('Household service not yet migrated to Supabase');
  },

  // Récupérer les foyers d'un utilisateur (où il est propriétaire ou membre)
  async getUserHouseholds(userId: string): Promise<Household[]> {
    try {
      const households: Household[] = [];

      // 1. Récupérer les foyers dont l'utilisateur est propriétaire
      const { data: ownedHouseholds, error: ownedError } = await supabase
        .from('households')
        .select('*')
        .eq('owner_id', userId);

      if (ownedError) {
        console.error('Error fetching owned households:', ownedError);
        throw ownedError;
      }

      if (ownedHouseholds && ownedHouseholds.length > 0) {
        households.push(...(ownedHouseholds as unknown as Household[]));
      }

      // 2. Récupérer les foyers dont l'utilisateur est membre
      const { data: memberHouseholds, error: memberError } = await supabaseRpc
        .rpc('get_user_member_households', {
          p_user_id: userId
        });

      if (memberError) {
        console.error('Error fetching member households:', memberError);
        throw memberError;
      }

      if (memberHouseholds && memberHouseholds.length > 0) {
        // RPC function returns households directly, not nested objects
        const memberHouseholdData = memberHouseholds as unknown as Household[];

        // Éviter les doublons (si l'utilisateur est à la fois propriétaire et membre)
        memberHouseholdData.forEach(household => {
          if (!households.find(h => h.id === household.id)) {
            households.push(household);
          }
        });
      }

      return households;
    } catch (error) {
      console.error('Error in getUserHouseholds:', error);
      return [];
    }
  },

  // Récupérer le foyer principal d'un utilisateur (le premier disponible)
  async getUserHousehold(userId: string): Promise<Household | null> {
    try {
      // Utiliser la méthode getUserHouseholds pour récupérer tous les foyers
      // et retourner le premier disponible
      const households = await this.getUserHouseholds(userId);

      if (households.length > 0) {
        return households[0];
      }

      return null;
    } catch (error) {
      console.error('Error fetching user household:', error);
      return null;
    }
  },

  // Récupérer les membres d'un foyer
  async getHouseholdMembers(householdId: string): Promise<UserProfile[]> {
    try {
      // First get member user IDs
      const { data: members, error: membersError } = await supabase
        .from('household_members')
        .select('user_id')
        .eq('household_id', householdId);

      if (membersError) {
        console.error('Error fetching household members:', membersError);
        throw membersError;
      }

      if (!members || members.length === 0) {
        return [];
      }

      // Then fetch user profiles separately
      const userIds = members.map(m => m.user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('*')
        .in('id', userIds);

      if (profilesError) {
        console.error('Error fetching user profiles:', profilesError);
        throw profilesError;
      }

      return (profiles as unknown as UserProfile[]) || [];
    } catch (error) {
      console.error('Error in getHouseholdMembers:', error);
      return [];
    }
  },

  // Récupérer un foyer par ID
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getHousehold(householdId: string): Promise<Household | null> {
    console.warn('Household service not yet migrated to Supabase');
    return null;
  },

  // Mettre à jour un foyer
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async updateHousehold(householdId: string, data: any): Promise<void> {
    throw new Error('Household service not yet migrated to Supabase');
  },

  // Supprimer un foyer
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async deleteHousehold(householdId: string): Promise<void> {
    throw new Error('Household service not yet migrated to Supabase');
  },

  // Ajouter un parent au foyer
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async addParent(householdId: string, parentData: any): Promise<void> {
    throw new Error('Household service not yet migrated to Supabase');
  },

  // Retirer un membre du foyer
  async removeHouseholdMember(householdId: string, userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('household_members')
        .delete()
        .eq('household_id', householdId)
        .eq('user_id', userId);

      if (error) {
        console.error('Error removing household member:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in removeHouseholdMember:', error);
      throw error;
    }
  },

  // Inviter un parent par email
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async inviteParent(householdId: string, email: string): Promise<void> {
    throw new Error('Household service not yet migrated to Supabase');
  },

  /**
   * Ouvre l'essai Harmonia+ du foyer et renvoie sa date de fin (ISO).
   *
   * L'erreur remonte telle quelle plutôt que d'être avalée en `null` : les deux
   * refus de la base veulent dire des choses très différentes pour le parent
   * (`trial_unavailable` = essai déjà consommé, il faut recharger le contexte
   * plutôt que réessayer ; `not_a_member` = un vrai problème de session), et
   * seul l'appelant sait quoi en dire à l'écran.
   */
};
